import { create } from 'zustand'
import { DaemonAPI, type Identity } from '@/api/daemon'
import { useUI } from '@/store/ui'

type Status = 'loading' | 'authed' | 'anon'

/** The one-login session state for the Apex console.
 *
 *  Apex holds no session machinery of its own — the Daemon (Apex's backend) is
 *  the only place a session is minted or read. The source of truth is the
 *  session cookie; `identity` merely mirrors /auth/me. There is no login action
 *  here: an anonymous visitor is SILENTLY sent to the gate (App.tsx's
 *  RequireAuth), the ONE door in the suite.
 *
 *  This exists ONLY because code-handoff needs a pre-flight probe: RequireAuth
 *  must know whether a session already exists (via /auth/me) and — if not —
 *  where the gate lives and whether code-handoff is on (via /auth/gate/status)
 *  BEFORE it can bounce. Settings/URLs still live in `useUI`; the Daemon URL is
 *  read from there so the two never disagree.
 */
interface AuthState {
  identity: Identity | null
  status: Status
  /** Where the gate lives (relayed from the backend's GATE_PUBLIC_ORIGIN);
   *  null until known. Needed for the silent bounce and for sign-out. */
  gateOrigin: string | null
  /** Apex's registered gate client id — present ONLY when code-handoff
   *  (APEX_GATE_SSO) is on and configured. Non-null means RequireAuth does the
   *  code-handoff redirect (client_id + callback); null means it falls back to
   *  the legacy plain-`next` shared-cookie bounce. */
  gateClientId: string | null
  api: () => DaemonAPI
  loadMe: () => Promise<void>
  /** Best-effort local sign-out (clears both cookies via the Daemon). The
   *  canonical sign-out then bounces to the gate's /logout — see SignOutButton. */
  logout: () => Promise<void>
}

export const useAuth = create<AuthState>()((set, get) => ({
  identity: null,
  status: 'loading',
  gateOrigin: null,
  gateClientId: null,
  api: () => new DaemonAPI(useUI.getState().daemonUrl),

  loadMe: async () => {
    try {
      const identity = await get().api().me()
      set({ identity, status: 'authed' })
    } catch {
      // Not signed in: learn where the gate lives (and whether code-handoff is
      // on) so RequireAuth can bounce there silently. Failure leaves gateOrigin
      // null and the app shows a quiet "gate unreachable" note instead of a form.
      let gateOrigin: string | null = null
      let gateClientId: string | null = null
      try {
        const status = await get().api().gateStatus()
        gateOrigin = status.gate_origin
        gateClientId = status.client_id ?? null
      } catch {
        gateOrigin = null
        gateClientId = null
      }
      set({ identity: null, status: 'anon', gateOrigin, gateClientId })
    }
  },

  logout: async () => {
    try {
      await get().api().logout()
    } catch {
      // Best effort — the gate's /logout still ends the suite-wide session.
    } finally {
      set({ identity: null, status: 'anon' })
    }
  },
}))
